import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import styles from './pref_integration.module.css';
import mcpConnectionsStore from '../../stores/mcp-connections.store';
import { IOAuthConnection } from '../../models/oauth-refresh-token.model';
import environmentService from '../../services/environment.service';

interface PlatformConfig {
  id: string;
  displayName: string;
  expectedClientNames: string[];
  connectAction: 'deeplink' | 'terminal';
  connectValue: string;
  docsUrl: string;
  instructions: string[];
  preCommand?: string;
}

function buildPlatforms(): PlatformConfig[] {
  const mcpUrl = environmentService.getMcpUrl();
  return [
    {
      id: 'claude',
      displayName: 'Claude',
      expectedClientNames: ['Claude', 'claude'],
      connectAction: 'terminal',
      connectValue: mcpUrl,
      docsUrl: 'https://www.shipbook.io/docs/mcp#claude',
      instructions: [
        'Open Claude and go to Settings → Connectors',
        'Add the URL below',
        'Log in and approve the connection',
      ],
    },
    {
      id: 'cursor',
      displayName: 'Cursor',
      expectedClientNames: ['Cursor', 'cursor'],
      connectAction: 'deeplink',
      connectValue: `cursor://anysphere.cursor-deeplink/mcp/install?name=shipbook&config=${btoa(JSON.stringify({ url: mcpUrl }))}`,
      docsUrl: 'https://www.shipbook.io/docs/mcp#cursor',
      instructions: [
        'Click the button below to open Cursor',
        'Approve the MCP installation when prompted',
        'Log in and approve the connection',
      ],
    },
    {
      id: 'vscode',
      displayName: 'GitHub Copilot (VS Code)',
      expectedClientNames: ['GitHub Copilot', 'vscode', 'VS Code', 'Visual Studio Code'],
      connectAction: 'deeplink',
      connectValue: `vscode:mcp/install?${encodeURIComponent(JSON.stringify({ name: 'shipbook', config: { type: 'http', url: mcpUrl } }))}`,
      docsUrl: 'https://www.shipbook.io/docs/mcp#github-copilot-vs-code',
      instructions: [
        'Click the button below to open VS Code',
        'Approve the MCP server installation when prompted',
        'A browser window will open — log in and approve the connection',
      ],
    },
    {
      id: 'codex',
      displayName: 'OpenAI Codex',
      expectedClientNames: ['Codex', 'codex', 'OpenAI Codex'],
      connectAction: 'terminal',
      connectValue: 'codex mcp login shipbook',
      docsUrl: 'https://www.shipbook.io/docs/mcp#codex',
      preCommand: `[mcp_servers.shipbook]\nurl = "${mcpUrl}"`,
      instructions: [
        'Add the following to ~/.codex/config.toml (shown below)',
        'Then run the login command',
        'A browser window will open — log in and approve the connection',
      ],
    },
  ];
}

const PLATFORMS = buildPlatforms();

// ─── Platform card ────────────────────────────────────────────────────────────

enum CardState { Idle, Expanded, Waiting, JustConnected }

interface PlatformCardProps {
  platform: PlatformConfig;
  connection: IOAuthConnection | undefined;
}

const PlatformCard = ({ platform, connection }: PlatformCardProps) => {
  const [cardState, setCardState] = useState<CardState>(CardState.Idle);
  const [copied, setCopied] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isConnected = !!connection;

  useEffect(() => {
    if (cardState !== CardState.Waiting) return;
    intervalRef.current = setInterval(async () => {
      await mcpConnectionsStore.load();
      const found = mcpConnectionsStore.connections.some(c =>
        platform.expectedClientNames.some(n => c.clientName?.toLowerCase().includes(n.toLowerCase()))
      );
      if (found) {
        clearInterval(intervalRef.current!);
        setCardState(CardState.JustConnected);
        setTimeout(() => setCardState(CardState.Idle), 2500);
      }
    }, 3000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [cardState, platform]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleConnect = () => {
    if (platform.connectAction === 'deeplink') {
      window.open(platform.connectValue, '_blank');
    }
    setCardState(CardState.Waiting);
  };

  const handleSetUp = () => setCardState(CardState.Expanded);
  const handleCollapse = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setCardState(CardState.Idle);
  };

  if (cardState === CardState.JustConnected) {
    return (
      <div className={`${styles.card} ${styles.cardSuccess}`}>
        <div className={styles.cardHeader}>
          <span className={styles.platformName}>{platform.displayName}</span>
          <span className={styles.statusConnected}>✓ Connected</span>
        </div>
      </div>
    );
  }

  if (isConnected && cardState === CardState.Idle) {
    return (
      <div className={`${styles.card} ${styles.cardConnected}`}>
        <div className={styles.cardHeader}>
          <span className={styles.platformName}>{platform.displayName}</span>
          <span className={styles.statusConnected}>Connected</span>
          <button className={styles.setupButton} onClick={handleSetUp}>
            Set up again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <span className={styles.platformName}>{platform.displayName}</span>
        {!isConnected && cardState === CardState.Idle && (
          <span className={styles.statusIdle}>Not connected</span>
        )}
        {cardState !== CardState.Idle && (
          <button className={styles.collapseButton} onClick={handleCollapse}>✕</button>
        )}
        {cardState === CardState.Idle && (
          <button className={styles.setupButton} onClick={handleSetUp}>Set up</button>
        )}
        <a className={styles.docsLink} href={platform.docsUrl} target="_blank" rel="noopener noreferrer">
          Docs
        </a>
      </div>

      {cardState === CardState.Expanded && (
        <div className={styles.cardBody}>
          <ol className={styles.steps}>
            {platform.instructions.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>

          {platform.preCommand && (
            <div className={styles.commandBlock}>
              <pre>{platform.preCommand}</pre>
              <button
                className={styles.copyButton}
                onClick={() => copyToClipboard(platform.preCommand!)}
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          )}

          {platform.connectAction === 'terminal' && (
            <div className={styles.commandBlock}>
              <pre>{platform.connectValue}</pre>
              <button
                className={styles.copyButton}
                onClick={() => copyToClipboard(platform.connectValue)}
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          )}

          <button className={styles.connectButton} onClick={handleConnect}>
            Create Connection
          </button>
        </div>
      )}

      {cardState === CardState.Waiting && (
        <div className={styles.cardBody}>
          <div className={styles.waitingRow}>
            <span className={styles.waitingSpinner} />
            <span>Waiting for connection...</span>
          </div>
          <button className={styles.cancelButton} onClick={handleCollapse}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const PrefIntegration = observer(() => {
  useEffect(() => {
    mcpConnectionsStore.load();
  }, []);

  const { connections } = mcpConnectionsStore;

  const getConnectionForPlatform = (platform: PlatformConfig) =>
    connections.find(c =>
      platform.expectedClientNames.some(n => c.clientName?.toLowerCase().includes(n.toLowerCase()))
    );

  return (
    <div className={styles.component}>
      <div className={styles.title}>MCP Integration</div>
      <div className={styles.subTitle}>
        Connect your AI coding assistant to Shipbook. Once connected, it can search your logs and errors directly from your editor.{' '}
        <a href="https://www.shipbook.io/docs/mcp" target="_blank" rel="noopener noreferrer">
          Learn more
        </a>
      </div>

      <div className={styles.section}>
        {PLATFORMS.map(platform => (
          <PlatformCard
            key={platform.id}
            platform={platform}
            connection={getConnectionForPlatform(platform)}
          />
        ))}
      </div>
    </div>
  );
});

export default PrefIntegration;
